import type React from "react";
import { useState } from "react";

import { ImageModal } from "@/components/Carousel/Carousel";
import { type FormImage } from "@/components/ImageInput/useImageInput";
import { Column, Columns } from "@/design-system/Columns";
import { Stack } from "@/design-system/Stack";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";

import { CommunityPostMultiImage, CommunityPostSingleImage } from "./CommunityPostImage";

const MAX_AMOUNT_IMAGE_SHOWN = 5;

interface CommunityPostGalleryProps {
  images: FormImage[];
  shouldLoad?: boolean;
}

export function CommunityPostGallery({ images, shouldLoad = true }: CommunityPostGalleryProps): React.ReactNode {
  const [isZoomModalOpened, zoomModalHandler] = useBool(false);
  const [zoomedImageIndex, setZoomedImageIndex] = useState<number>();

  useKey("Escape", zoomModalHandler.setFalse);

  let partitionedImages = [images];
  if (images.length > 3) {
    partitionedImages = [images.slice(0, 2), images.slice(2, MAX_AMOUNT_IMAGE_SHOWN)];
  }

  return (
    <>
      {/* Single image setup */}
      {images.length === 1 && (
        <CommunityPostSingleImage
          image={images[0]}
          shouldLoad={shouldLoad}
          onClick={() => {
            setZoomedImageIndex(0);
            zoomModalHandler.setTrue();
          }}
        />
      )}

      {/* Multiple images grid setup */}
      {images.length > 1 && (
        <Stack gap="sm">
          {partitionedImages.map((imageGroup, imageGroupIndex) => (
            <Columns key={imageGroup.map((image) => image.url).toString()} gap="sm">
              {imageGroup.map((currImage, imageIndex) => {
                const isLast = imageGroupIndex === partitionedImages.length - 1 && imageIndex === imageGroup.length - 1;
                const absoluteImageIndex =
                  partitionedImages
                    .slice(0, imageGroupIndex)
                    .reduce((imageCount, group) => imageCount + group.length, 0) + imageIndex;

                return (
                  <Column key={currImage.url}>
                    <CommunityPostMultiImage
                      image={currImage}
                      amountImagesAfter={images.length - MAX_AMOUNT_IMAGE_SHOWN}
                      onClick={() => {
                        setZoomedImageIndex(absoluteImageIndex);
                        zoomModalHandler.setTrue();
                      }}
                      {...{ shouldLoad, isLast }}
                    />
                  </Column>
                );
              })}
            </Columns>
          ))}
        </Stack>
      )}
      <ImageModal
        images={images}
        zoomedImageIndex={zoomedImageIndex}
        isOpened={isZoomModalOpened}
        onOpenChange={zoomModalHandler.set}
      />
    </>
  );
}
